package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.ConflictType
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.schedule.LabourCode
import com.evoapps.evoshift.domain.util.gapMinutesTo
import com.evoapps.evoshift.domain.util.overlaps

data class RosterConflict(
    val userId: String,
    val shiftId: String,
    val otherShiftId: String,
    val type: ConflictType
)

data class RosterConflictReport(
    val conflicts: List<RosterConflict>,
    val description: String
) {
    val shiftsWithConflicts: List<String> get() = conflicts.flatMap { listOf(it.shiftId, it.otherShiftId) }.distinct()
}

/**
 * Finds, per employee, shifts of one roster that overlap (DOUBLE_BOOKED) or leave less than the
 * minimum daily rest between them (REST_VIOLATION, 11 hours as in Polish labour law).
 */
class DetectRosterConflictsUseCase(
    private val shiftRepository: ShiftRepository
) {
    companion object {
        /** The labour code's daily rest ([LabourCode.DAILY_REST_HOURS]), as auto-assign keeps it. */
        const val MINIMUM_REST_MINUTES = LabourCode.DAILY_REST_HOURS * 60L
    }

    suspend operator fun invoke(rosterId: String): Result<RosterConflictReport> = runCatching {
        val shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val conflicts = shifts.filter { it.userId != null }
            .groupBy { it.userId!! }
            .flatMap { (userId, userShifts) ->
                userShifts.sortedBy { it.plannedTime.start }.zipWithNext().mapNotNull { (current, next) ->
                    when {
                        current.plannedTime.overlaps(next.plannedTime) ->
                            RosterConflict(userId, current.id, next.id, ConflictType.DOUBLE_BOOKED)
                        current.plannedTime.gapMinutesTo(next.plannedTime) < MINIMUM_REST_MINUTES ->
                            RosterConflict(userId, current.id, next.id, ConflictType.REST_VIOLATION)
                        else -> null
                    }
                }
            }
        RosterConflictReport(conflicts, "Found ${conflicts.size} potential conflicts.")
    }
}
